import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import { defineMessages, injectIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { mainStatusText } from "../views/Job/Job.messages";
import useJobs from "../hooks/useJobs";
import useFormatter from "../hooks/useFormatter";
import useMessages from "../hooks/useMessages";

// strings for translations
const messages = defineMessages({
    chooseInvoiceLabel: {
        id: "Laskun numero (valinnainen)",
        defaultMessage: "Laskun numero (valinnainen)"
    },
    preselectedInvoiceLabel: {
        id: "Laskun numero",
        defaultMessage: "Laskun numero"
    },
    noInvoiceOption: {
        id: "Asioin toisen puolesta / Ei laskunumeroa",
        defaultMessage: "Asioin toisen puolesta / Ei laskunumeroa"
    }
});

export { messages };

const SEARCH_DEBOUNCE_MS = 750;
const SEARCH_MIN_LENGTH = 4; // matches the Dashboard search field's threshold

/**
 * Custom typeahead for optionally selecting one of the user's open invoices/jobs.
 *
 * Shows the user's open invoices itself (like the Dashboard invoices tab) and lets typing
 * search for more, unless a job is preselected - in which case it shows that job directly
 * (it might not be among the fetched open invoices at all, e.g. if it's already paid or
 * archived) and skips fetching/searching altogether.
 */
const JobSelectorDropdown = (props) => {
    const lng = props.intl.formatMessage;
    const { messageCenterMessages } = useMessages();
    const [jobs, setJobs] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasFetchedInvoices, setHasFetchedInvoices] = useState(false);
    const [searchText, setSearchText] = useState('');
    const { fetchOpenInvoices, searchInvoices, getJobCurrency } = useJobs();
    const { formatCurrency } = useFormatter();
    const isPreselected = !!props.job;

    /**
     * Resolve a human-readable label for the job's main status (invoice/reminder/collection/paid).
     * @param job
     * @return {string}
     */
    const getTypeLabel = (job) => {
        switch (job.job_main_statuscode) {
            case 0: return lng(mainStatusText.invoice);
            case 1: return lng(mainStatusText.reminder);
            case 2: return lng(mainStatusText.collection);
            case 101: return lng(mainStatusText.paid);
            default: return '';
        }
    }

    const selectorLabel = isPreselected ? lng(messages.preselectedInvoiceLabel) : lng(messages.chooseInvoiceLabel);

    const selectedJob = isPreselected ? props.job : props.value;

    const displayValue = selectedJob ? selectedJob.billnum ?? '' : lng(messages.noInvoiceOption);

    /**
     * Check if job has message flow already started
     * @param {object} job
     * @returns {boolean}
     */
    const jobHasMessages = (job) => {
        const jobMessages = messageCenterMessages.filter(msg => msg.jobid === job.jobid.toString());
        return jobMessages?.length > 0 && jobMessages.some(msg => msg.current_status?.status !== 'Closed');
    }

    /**
     * Set jobs by filtering out jobs that already have message flow started
     * @param {object[]} jobs
     */
    const setFilteredJobs = (jobs) => {
        setJobs(jobs.filter(job => !jobHasMessages(job)));
    }

    // Keep the field showing the current selection whenever it's not being actively edited
    useEffect(() => {
        if (!menuOpen) {
            setSearchText(displayValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayValue, menuOpen]);

    // Load the default open invoices list once, the first time the dropdown is opened
    useEffect(() => {
        if (!menuOpen || isPreselected || hasFetchedInvoices) {
            return;
        }
        setHasFetchedInvoices(true);
        setLoading(true);
        fetchOpenInvoices().then(setFilteredJobs).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOpen]);

    // Search with a debounce, same as the Dashboard search field: only search once the user
    // has paused typing, and only for a search term of a few characters or more, otherwise
    // fall back to showing the default open invoices list.
    const debouncedSearch = useDebouncedCallback((value) => {
        if (value.length >= SEARCH_MIN_LENGTH) {
            setLoading(true);
            searchInvoices(value).then(setFilteredJobs).finally(() => setLoading(false));
        } else if (value.length === 0) {
            setLoading(true);
            fetchOpenInvoices().then(setFilteredJobs).finally(() => setLoading(false));
        }
    }, props.timeout ?? SEARCH_DEBOUNCE_MS);

    const handleInputChange = (e) => {
        setSearchText(e.target.value);
        setMenuOpen(true);
        if (e.target.value.length === 0) {
            props.onChange(null);
        }
        debouncedSearch(e.target.value);
    }

    const handleSelect = (job) => {
        props.onChange(job);
        setMenuOpen(false);
    }

    const jobRowElement = (job) => (
        <button
            type="button"
            key={job.jobid}
            className="dropdown-item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelect(job)}
        >
            <div className="d-flex justify-content-between">
                <div>
                    <div className="fw-bold">{job.payee?.name}</div>
                    <div className="text-muted small">
                        {job.billnum} {getTypeLabel(job)}
                    </div>
                </div>
                <div className="fw-bold">
                    {job.openamount > 0 ? formatCurrency(job.openamount, getJobCurrency(job)) : ''}
                </div>
            </div>
        </button>
    )

    return (
        <div className="form-floating mb-3">
            <Form.Control
                id="message-center-job-option"
                className="job-selector-toggle"
                autoComplete="off"
                placeholder={selectorLabel}
                disabled={isPreselected}
                value={searchText}
                onChange={handleInputChange}
                onFocus={() => setMenuOpen(true)}
                onBlur={() => setMenuOpen(false)}
                aria-label={selectorLabel}
            />
            <Form.Label htmlFor="message-center-job-option">{selectorLabel}</Form.Label>
            {menuOpen && (
                <div className="dropdown-menu show job-selector-dropdown-menu">
                    <button
                        type="button"
                        className="dropdown-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(null)}
                    >
                        {lng(messages.noInvoiceOption)}
                    </button>
                    {loading && (
                        <div className="text-center py-2">
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}
                    {!loading && jobs.map(job => jobRowElement(job))}
                </div>
            )}
        </div>
    );
};

JobSelectorDropdown.propTypes = {
    'intl': PropTypes.object.isRequired,
    'value': PropTypes.object,
    'onChange': PropTypes.func.isRequired,
    'job': PropTypes.object,
    'timeout': PropTypes.number
};

export default injectIntl(JobSelectorDropdown);
